import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
    const [isLoading, setIsLoading] = useState(true);

    const isAuthenticated = !!user && !!token;

    // Validate stored token on mount
    useEffect(() => {
        const validateToken = async () => {
            const storedToken = localStorage.getItem('auth_token');
            if (!storedToken) {
                setIsLoading(false);
                return;
            }

            try {
                const response = await axios.get(`${API}/auth/me`, {
                    headers: { Authorization: `Bearer ${storedToken}` }
                });
                setUser(response.data);
                setToken(storedToken);
            } catch (err) {
                // Token expired or invalid
                console.warn('Stored token invalid, clearing auth state');
                localStorage.removeItem('auth_token');
                setToken(null);
                setUser(null);
            } finally {
                setIsLoading(false);
            }
        };

        validateToken();
    }, []);

    const login = useCallback(async (googleCredential) => {
        try {
            const response = await axios.post(`${API}/auth/google`, {
                token: googleCredential
            });

            const { access_token } = response.data;
            setUser(response.data);
            setToken(access_token);
            localStorage.setItem('auth_token', access_token);
            return response.data;
        } catch (err) {
            console.error('Login failed:', err);
            throw err;
        }
    }, []);

    const logout = useCallback(() => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('auth_token');
    }, []);

    // Helper to get auth headers for API calls
    const getAuthHeaders = useCallback(() => {
        if (!token) return {};
        return { Authorization: `Bearer ${token}` };
    }, [token]);

    const value = {
        user,
        token,
        isAuthenticated,
        isLoading,
        login,
        logout,
        getAuthHeaders
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
